import { useMemo, useState } from 'react';
import { toAssetUrl } from '../../utils/assetBase';
import {
    normalizeUrl, FacebookIcon, InstagramIcon, WhatsAppIcon, PhoneIcon, PinIcon, ClockIcon, GoldStar,
    DAY_ORDER, DAY_LABELS,
} from './QrCover';

// QR giriş ekranı V2 — modern: tam enli hero (şəkil/brend gradient) + üstə çıxan ağ panel.
// V1 ilə eyni məlumatlar. STANDART dizayndır: qr_theme.cover_design açıq 'v1' deyilsə bu göstərilir.

const DEFAULT_BRAND = '#0090bb';

const hexToRgb = (hex) => {
    const h = String(hex || '').replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.slice(0, 6);
    const n = parseInt(full, 16);
    if (!Number.isFinite(n) || full.length !== 6) return [0, 144, 187];
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const rgba = (hex, a) => { const [r, g, b] = hexToRgb(hex); return `rgba(${r},${g},${b},${a})`; };
// Rəngi qaraya doğru tündləşdir (0..1)
const shade = (hex, amt) => {
    const [r, g, b] = hexToRgb(hex).map((c) => Math.round(c * (1 - amt)));
    return `rgb(${r},${g},${b})`;
};

const toMin = (t) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

// Bakı vaxtı ilə indi: { dayKey: 'mon'.., minutes }
const nowInBaku = () => {
    try {
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Asia/Baku', weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
        }).formatToParts(new Date());
        const get = (t) => parts.find((p) => p.type === t)?.value || '';
        const wk = get('weekday').slice(0, 3).toLowerCase();
        return { dayKey: wk, minutes: (Number(get('hour')) % 24) * 60 + Number(get('minute')) };
    } catch {
        const d = new Date();
        return { dayKey: DAY_ORDER[(d.getDay() + 6) % 7], minutes: d.getHours() * 60 + d.getMinutes() };
    }
};

// Bir günün iş vaxtı pəncərəsi: { open, close } və ya null (bağlı)
const windowFor = (hs, dayKey) => {
    if (!hs) return null;
    if (hs.mode === 'everyday') {
        const e = hs.everyday || {};
        return (e.open && e.close) ? { open: e.open, close: e.close } : null;
    }
    const d = hs.days?.[dayKey];
    return (d && d.on && d.open && d.close) ? { open: d.open, close: d.close } : null;
};

// Açıq/bağlı statusu (gecəyarısını keçən saatlar da nəzərə alınır)
const computeStatus = (hs) => {
    if (!hs) return null;
    const { dayKey, minutes } = nowInBaku();
    const idx = DAY_ORDER.indexOf(dayKey);
    const yKey = DAY_ORDER[(idx + 6) % 7];
    const today = windowFor(hs, dayKey);
    const yest = windowFor(hs, yKey);

    // Dünən gecəyarısını keçən növbə hələ davam edir?
    if (yest) {
        const o = toMin(yest.open); const c = toMin(yest.close);
        if (o !== null && c !== null && c <= o && minutes < c) return { open: true, today };
    }
    if (today) {
        const o = toMin(today.open); const c = toMin(today.close);
        if (o !== null && c !== null) {
            const isOpen = c > o ? (minutes >= o && minutes < c) : (minutes >= o);
            return { open: isOpen, today, opensLater: !isOpen && minutes < o };
        }
    }
    return { open: false, today: null };
};

// Şəkil olmayanda hero fonu: incə xətli yemək ikonları naxışı (Lucide ikonları, ISC lisenziya).
const FOOD_ICON_PATHS = [
    'M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2 M7 2v20 M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7', // çəngəl-bıçaq
    'M10 2v2 M14 2v2 M6 2v2 M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1', // qəhvə
    'M8 22h8 M7 10h10 M12 15v7 M12 15a5 5 0 0 0 5-5c0-2-.5-4-2-8H9c-1.5 4-2 6-2 8a5 5 0 0 0 5 5Z', // qədəh
    'M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589 5 5 0 0 0-9.186 0 4 4 0 0 0-2.134 7.588c.411.198.727.585.727 1.041V20a1 1 0 0 0 1 1Z M6 17h12', // aşpaz papağı
    'M6.5 12c.94-3.46 4.94-6 8.5-6 3.56 0 6.06 2.54 7 6-.94 3.47-3.44 6-7 6s-7.56-2.53-8.5-6Z M18 12v.5 M16 17.93a9.77 9.77 0 0 1 0-11.86 M7 10.67C7 8 5.58 5.97 2.73 5.5c-1 1.5-1 5 .23 6.5-1.24 1.5-1.24 5-.23 6.5C5.58 18.03 7 16 7 13.33', // balıq
    'M12 21a9 9 0 0 0 9-9H3a9 9 0 0 0 9 9Z M7 21h10 M19.5 12 22 6 M16.25 3c.27.1.8.53.75 1.36-.06.83-.93 1.2-1 2.02-.05.78.34 1.24.73 1.62 M11.25 3c.27.1.8.53.74 1.36-.05.83-.93 1.2-.98 2.02-.06.78.33 1.24.72 1.62', // şorba
    'M17.775 5.654a15.68 15.68 0 0 0-12.121 12.12 M21.964 20.732a1 1 0 0 1-1.232 1.232l-18-5a1 1 0 0 1-.695-1.232A19.68 19.68 0 0 1 15.732 2.037a1 1 0 0 1 1.232.695z M12 14l-1 1 M13.75 18.25l-1.25 1.42', // pizza
    'M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12', // yarpaq
    'm7 11 4.08 10.35a1 1 0 0 0 1.84 0L17 11 M17 7A5 5 0 0 0 7 7 M17 7a2 2 0 0 1 0 4H7a2 2 0 0 1 0-4', // dondurma
];
// [x, y, bucaq] — 220×220 kafel daxilində səpələnmiş
const FOOD_ICON_POS = [[18, 16, -12], [96, 8, 10], [172, 28, -6], [48, 84, 8], [128, 78, -15], [190, 108, 12], [12, 150, 14], [88, 158, -8], [166, 176, 6]];
const foodPattern = (opacity) => {
    const g = FOOD_ICON_PATHS.map((d, i) => {
        const [x, y, r] = FOOD_ICON_POS[i];
        return `<g transform='translate(${x} ${y}) rotate(${r} 12 12) scale(1.3)'><path d='${d}'/></g>`;
    }).join('');
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220' viewBox='0 0 220 220'><g fill='none' stroke='#ffffff' stroke-opacity='${opacity}' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'>${g}</g></svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};
const PATTERN_HERO = foodPattern(0.16);
const PATTERN_PANEL = foodPattern(0.07);

const MapIcon = ({ className = 'h-5 w-5' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
        <line x1="8" y1="2" x2="8" y2="18" />
        <line x1="16" y1="6" x2="16" y2="22" />
    </svg>
);

const ArrowIcon = ({ className = 'h-5 w-5' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
    </svg>
);

const MenuBookIcon = ({ className = 'h-5 w-5' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M2 4h6a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H2z" />
        <path d="M22 4h-6a4 4 0 0 0-4 4v13a3 3 0 0 1 3-3h7z" />
    </svg>
);

const ChevronIcon = ({ className = 'h-4 w-4', open }) => (
    <svg className={className} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const QrCoverV2 = ({ tenant, token, occupancy, onEnter }) => {
    const [hoursOpen, setHoursOpen] = useState(false);

    const th = tenant?.qr_theme || {};
    const BRAND = (typeof th.primary === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(th.primary)) ? th.primary : DEFAULT_BRAND;
    const BRAND_DARK = shade(BRAND, 0.35);

    const logo = tenant?.qr_cover_logo_url || tenant?.login_image_url || '';
    const name = tenant?.name || 'Restoran';
    const address = tenant?.qr_cover_address || '';
    const hoursText = tenant?.qr_cover_hours || '';
    const phone = tenant?.phone || '';
    const fb = normalizeUrl(tenant?.qr_cover_facebook);
    const ig = normalizeUrl(tenant?.qr_cover_instagram);
    const waDigits = String(tenant?.qr_cover_whatsapp || '').replace(/\D/g, '');
    const waIntl = waDigits ? (waDigits.startsWith('994') ? waDigits : waDigits.startsWith('0') ? '994' + waDigits.slice(1) : '994' + waDigits) : '';
    const wa = waIntl ? `https://wa.me/${waIntl}` : '';
    const mapsUrl = address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name} ${address}`)}` : '';

    const bookingLabel = String(tenant?.qr_cover_booking_label || '').trim();
    const showBooking = Number(tenant?.qr_cover_booking_enabled || 0) === 1 && bookingLabel && token;
    const banquetLabel = String(tenant?.qr_cover_banquet_label || '').trim();
    const showBanquet = Number(tenant?.qr_cover_banquet_enabled || 0) === 1 && banquetLabel && token;
    const gReviewUrl = Number(tenant?.google_review_enabled || 0) === 1 ? (tenant?.google_review_url || '') : '';
    const twoGisUrl = String(tenant?.twogis_url || '').trim();

    const hoursStruct = useMemo(() => {
        try { const j = tenant?.qr_cover_hours_json; if (j) return typeof j === 'string' ? JSON.parse(j) : j; } catch { /* yanlış JSON */ }
        return null;
    }, [tenant]);
    const status = useMemo(() => computeStatus(hoursStruct), [hoursStruct]);
    const todayKey = useMemo(() => nowInBaku().dayKey, []);

    // Fon şəkli + superadmin örtüyü (rəng + faiz)
    const coverBg = tenant?.qr_cover_bg_url ? toAssetUrl(tenant.qr_cover_bg_url) : '';
    const ovOp = Number.isFinite(Number(th.cover_overlay_opacity)) ? Math.max(0, Math.min(90, Number(th.cover_overlay_opacity))) / 100 : 0.2;
    const ovColor = th.cover_overlay === 'blue' ? BRAND : '#ffffff';

    // Şəkil varsa hero hündür (şəkil görünsün), yoxdursa qısa (boş gradient olmasın)
    const heroH = coverBg ? 'min(58vh, 460px)' : 'min(40vh, 320px)';

    const occ = occupancy && occupancy.total > 0 ? (() => {
        const p = occupancy.percent;
        const free = Math.max(0, occupancy.total - occupancy.occupied);
        const dot = p >= 70 ? '#f43f5e' : p >= 40 ? '#f59e0b' : '#10b981';
        return { p, free, dot };
    })() : null;

    const quick = [
        phone && { key: 'tel', label: 'Zəng', href: `tel:${phone}`, icon: <PhoneIcon className="h-5 w-5" />, bg: rgba(BRAND, 0.1), fg: BRAND },
        mapsUrl && { key: 'map', label: 'Xəritə', href: mapsUrl, ext: true, icon: <MapIcon className="h-5 w-5" />, bg: rgba(BRAND, 0.1), fg: BRAND },
        wa && { key: 'wa', label: 'WhatsApp', href: wa, ext: true, icon: <WhatsAppIcon className="h-5 w-5" />, bg: '#e8f9ee', fg: '#1ea952' },
        ig && { key: 'ig', label: 'Instagram', href: ig, ext: true, icon: <InstagramIcon className="h-5 w-5" />, bg: '#fdeef4', fg: '#d6246e' },
        fb && { key: 'fb', label: 'Facebook', href: fb, ext: true, icon: <FacebookIcon className="h-5 w-5" />, bg: '#e9f1fe', fg: '#1877F2' },
    ].filter(Boolean);

    const tiles = [
        showBooking && { key: 'bk', emoji: '🏡', label: bookingLabel, href: `/evler?t=${encodeURIComponent(token)}` },
        showBanquet && { key: 'bq', emoji: '🥂', label: banquetLabel, href: `/banket?t=${encodeURIComponent(token)}` },
    ].filter(Boolean);

    const hasHours = Boolean(hoursStruct || hoursText);

    return (
        <div className="qr-cover-root min-h-screen w-full bg-[#f7f8fa]" style={{ touchAction: 'manipulation' }}>
            <style>{`
                @keyframes qv2Up { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
                @keyframes qv2Shine { 0%, 60% { transform: translateX(-130%) skewX(-20deg); } 100% { transform: translateX(260%) skewX(-20deg); } }
                @keyframes qv2Drift { from { background-position: 0 0; } to { background-position: 220px 220px; } }
                @keyframes qv2DriftLg { from { background-position: 0 0; } to { background-position: 340px 340px; } }
                .qv2-pat { background-size: 220px 220px; animation: qv2Drift 70s linear infinite; }
                @media (min-width: 1024px) { .qv2-pat { background-size: 340px 340px; animation-name: qv2DriftLg; animation-duration: 100s; } }
                @keyframes qv2Pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,.55); } 70% { box-shadow: 0 0 0 7px rgba(16,185,129,0); } }
                .qv2-up { opacity: 0; animation: qv2Up .55s cubic-bezier(.2,.7,.2,1) forwards; }
                .qv2-cta::after { content: ''; position: absolute; inset: 0; pointer-events: none;
                    background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,.28) 50%, transparent 70%);
                    animation: qv2Shine 3.6s ease-in-out infinite; }
                .qv2-live { animation: qv2Pulse 2s infinite; }
                @media (prefers-reduced-motion: reduce) {
                    .qv2-up { animation: none; opacity: 1; } .qv2-cta::after, .qv2-live, .qv2-pat { animation: none; }
                }
            `}</style>

            <div className="flex min-h-screen w-full flex-col lg:flex-row lg:items-start">
                {/* ── HERO ─────────────────────────────────────────── */}
                <section className="relative isolate flex min-h-[var(--hero-h)] flex-col overflow-hidden text-white md:min-h-[52vh] lg:sticky lg:top-0 lg:h-screen lg:min-h-0 lg:w-[58%] lg:flex-shrink-0" style={{ '--hero-h': heroH }}>
                    {coverBg ? (
                        <>
                            <div className="absolute inset-0 -z-10" style={{ backgroundImage: `url("${coverBg}")`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                            <div className="absolute inset-0 -z-10" style={{ background: ovColor, opacity: ovOp }} />
                        </>
                    ) : (
                        <div className="absolute inset-0 -z-10" style={{ background: `radial-gradient(120% 80% at 85% 0%, ${rgba(BRAND, 0.55)} 0%, transparent 55%), radial-gradient(90% 70% at 0% 100%, ${rgba(BRAND, 0.35)} 0%, transparent 60%), linear-gradient(160deg, ${BRAND} 0%, ${BRAND_DARK} 100%)` }}>
                            <div className="qv2-pat absolute inset-0" style={{ backgroundImage: PATTERN_HERO }} />
                            <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
                            <div className="absolute -left-10 bottom-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
                        </div>
                    )}
                    {/* Yazılar oxunsun deyə yuxarı və aşağı tündləşmə */}
                    <div className="absolute inset-0 -z-10" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,.28) 0%, rgba(0,0,0,0) 28%, rgba(0,0,0,0) 42%, rgba(0,0,0,.62) 100%)' }} />

                    <div className="flex flex-1 flex-col justify-between px-5 pb-12 pt-5 md:px-10 md:pb-16 md:pt-8 lg:px-14 lg:pb-14 lg:pt-10">
                        {/* Üst sətir: loqo + doluluq */}
                        <div className="qv2-up flex items-start justify-between gap-3">
                            <div className="h-16 w-16 overflow-hidden rounded-2xl bg-white p-1 shadow-lg ring-1 ring-black/5 lg:h-24 lg:w-24 lg:rounded-3xl">
                                {logo ? (
                                    <img src={toAssetUrl(logo)} alt={name} className="h-full w-full rounded-xl object-cover"
                                        onError={(e) => { e.target.style.display = 'none'; }} />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center rounded-xl text-2xl font-black" style={{ color: BRAND }}>
                                        {name.charAt(0).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            {occ && (
                                <div className="rounded-2xl border border-white/25 bg-white/15 px-3 py-2 text-right backdrop-blur-md">
                                    <div className="flex items-center justify-end gap-1.5 text-[13px] font-bold">
                                        <span className="h-2 w-2 rounded-full" style={{ background: occ.dot }} />
                                        {occ.p}% dolu
                                    </div>
                                    <div className="text-[11px] font-medium text-white/80">{occ.free} masa boşdur</div>
                                </div>
                            )}
                        </div>

                        {/* Alt: ad + status */}
                        <div>
                            <p className="qv2-up text-sm font-medium text-white/90 lg:text-lg" style={{ animationDelay: '.05s', textShadow: '0 1px 6px rgba(0,0,0,.5)' }}>Xoş gəlmisiniz</p>
                            <h1 className="qv2-up mt-1 text-[34px] font-extrabold leading-[1.08] tracking-tight md:text-5xl lg:text-7xl" style={{ animationDelay: '.1s', textShadow: '0 2px 12px rgba(0,0,0,.35)' }}>
                                {name}
                            </h1>
                            {status && (
                                <div className="qv2-up mt-3 flex flex-wrap items-center gap-2" style={{ animationDelay: '.15s' }}>
                                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold lg:px-4 lg:py-1.5 lg:text-sm ${status.open ? 'bg-emerald-500/90' : 'bg-white/20 backdrop-blur-md'}`}>
                                        <span className={`h-1.5 w-1.5 rounded-full ${status.open ? 'qv2-live bg-white' : 'bg-rose-400'}`} />
                                        {status.open ? 'Açıqdır' : 'Bağlıdır'}
                                    </span>
                                    {status.today && (
                                        <span className="text-xs font-semibold text-white/90 lg:text-sm" style={{ textShadow: '0 1px 6px rgba(0,0,0,.55)' }}>
                                            {status.opensLater ? `Bu gün açılır: ${status.today.open}` : `Bu gün ${status.today.open} – ${status.today.close}`}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* ── PANEL ────────────────────────────────────────── */}
                <main className="qr-cover-root relative -mt-7 flex-1 rounded-t-[28px] bg-[#f7f8fa] px-5 pb-8 pt-6 md:px-10 lg:mt-0 lg:flex lg:min-h-screen lg:flex-col lg:justify-center lg:overflow-hidden lg:rounded-none lg:bg-transparent lg:px-12 lg:py-12">
                    {/* Kompüter: sağ panelin fonu — soldakı şəklin bulanıq versiyası (və ya brend gradient) + incə naxış */}
                    <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden lg:block" style={{ background: BRAND_DARK }}>
                        {coverBg && (
                            <div className="absolute -inset-16" style={{ backgroundImage: `url("${coverBg}")`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(48px) saturate(1.35)', transform: 'scale(1.1)' }} />
                        )}
                        <div className="absolute inset-0" style={{
                            background: coverBg
                                ? `linear-gradient(155deg, ${rgba(BRAND, 0.55)} 0%, rgba(8,18,28,0.72) 60%, rgba(8,18,28,0.85) 100%)`
                                : `radial-gradient(70% 55% at 85% 10%, ${rgba(BRAND, 0.65)} 0%, transparent 60%), radial-gradient(60% 50% at 10% 95%, ${rgba(BRAND, 0.45)} 0%, transparent 60%), linear-gradient(160deg, ${BRAND_DARK} 0%, #08121c 100%)`,
                        }} />
                        {coverBg ? (
                            <div className="absolute inset-0 opacity-60" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.09) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
                        ) : (
                            <div className="qv2-pat absolute inset-0" style={{ backgroundImage: PATTERN_PANEL }} />
                        )}
                        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full blur-3xl" style={{ background: rgba(BRAND, 0.35) }} />
                        <div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-white/5 blur-3xl" />
                    </div>
                    <div className="relative mx-auto w-full max-w-md md:max-w-xl lg:max-w-[460px]">
                    {/* Kompüter: panel başlığı */}
                    <div className="qv2-up mb-5 hidden lg:block" style={{ animationDelay: '.15s' }}>
                        <h2 className="text-2xl font-bold text-white">Nə etmək istərdiniz?</h2>
                        <p className="mt-1 text-sm text-white/60">Menyuya baxın, bizimlə əlaqə saxlayın və ya rəy yazın.</p>
                    </div>
                    {/* Əsas düymə */}
                    <button
                        type="button"
                        onClick={onEnter}
                        className="qv2-up qv2-cta relative flex h-[60px] w-full items-center gap-3 overflow-hidden rounded-2xl pl-4 pr-2 text-left text-white transition-transform active:scale-[0.98] lg:h-16 lg:ring-1 lg:ring-white/30"
                        style={{ background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`, boxShadow: `0 12px 28px -10px ${rgba(BRAND, 0.65)}`, animationDelay: '.2s' }}
                    >
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15"><MenuBookIcon className="h-5 w-5" /></span>
                        <span className="flex-1 text-[17px] font-bold">Menyuya bax</span>
                        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white" style={{ color: BRAND }}><ArrowIcon className="h-5 w-5" /></span>
                    </button>

                    {/* Əlavə bölmələr (A-frame, banket) */}
                    {tiles.length > 0 && (
                        <div className={`qv2-up mt-3 grid gap-3 ${tiles.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`} style={{ animationDelay: '.25s' }}>
                            {tiles.map((t) => (
                                <a key={t.key} href={t.href}
                                    className={`flex rounded-2xl bg-white p-3.5 shadow-[0_1px_3px_rgba(16,24,40,.08)] ring-1 ring-black/[0.04] lg:shadow-[0_18px_40px_-18px_rgba(0,0,0,.55)] lg:ring-white/10 transition-transform active:scale-[0.98] ${tiles.length > 1 ? 'flex-col items-start gap-2.5' : 'items-center gap-3'}`}>
                                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-xl" style={{ background: rgba(BRAND, 0.1) }}>{t.emoji}</span>
                                    <span className="flex w-full min-w-0 flex-1 items-end justify-between gap-2">
                                        <span className="line-clamp-2 text-sm font-bold leading-snug text-gray-900">{t.label}</span>
                                        <span className="flex-shrink-0" style={{ color: BRAND }}><ArrowIcon className="h-4 w-4" /></span>
                                    </span>
                                </a>
                            ))}
                        </div>
                    )}

                    {/* Sürətli əlaqə */}
                    {quick.length > 0 && (
                        <div className="qv2-up mt-5 rounded-2xl bg-white p-4 shadow-[0_1px_3px_rgba(16,24,40,.08)] ring-1 ring-black/[0.04] lg:shadow-[0_18px_40px_-18px_rgba(0,0,0,.55)] lg:ring-white/10" style={{ animationDelay: '.3s' }}>
                            <div className="flex justify-around gap-1">
                                {quick.map((q) => (
                                    <a key={q.key} href={q.href} {...(q.ext ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                        className="flex min-w-0 flex-1 flex-col items-center gap-1.5 transition-transform active:scale-95">
                                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: q.bg, color: q.fg }}>{q.icon}</span>
                                        <span className="truncate text-[11px] font-semibold text-gray-600">{q.label}</span>
                                    </a>
                                ))}
                            </div>
                            {address && (
                                <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                                    className="mt-4 flex items-start gap-2.5 border-t border-gray-100 pt-3.5 text-left">
                                    <PinIcon className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: BRAND }} />
                                    <span className="text-[13px] font-medium leading-snug text-gray-700">{address}</span>
                                </a>
                            )}
                        </div>
                    )}

                    {/* Google rəyi */}
                    {gReviewUrl && (
                        <button type="button" onClick={() => window.open(gReviewUrl, '_blank', 'noopener,noreferrer')}
                            className="qv2-up mt-3 flex w-full items-center gap-3 rounded-2xl bg-white p-4 text-left shadow-[0_1px_3px_rgba(16,24,40,.08)] ring-1 ring-black/[0.04] lg:shadow-[0_18px_40px_-18px_rgba(0,0,0,.55)] lg:ring-white/10 transition-transform active:scale-[0.98]"
                            style={{ animationDelay: '.35s' }}>
                            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gray-50">
                                <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
                                </svg>
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="block text-sm font-bold text-gray-900">Bizi qiymətləndirin</span>
                                <span className="text-[11px] font-medium text-gray-500">Google-da rəy yazın</span>
                            </span>
                            <span className="flex gap-0.5">{[0, 1, 2, 3, 4].map((i) => <GoldStar key={i} size={16} />)}</span>
                        </button>
                    )}

                    {/* 2GIS firma səhifəsi (superadmin link yazıbsa) */}
                    {twoGisUrl && (
                        <button type="button" onClick={() => window.open(twoGisUrl, '_blank', 'noopener,noreferrer')}
                            className="qv2-up mt-3 flex w-full items-center gap-3 rounded-2xl bg-white p-4 text-left shadow-[0_1px_3px_rgba(16,24,40,.08)] ring-1 ring-black/[0.04] lg:shadow-[0_18px_40px_-18px_rgba(0,0,0,.55)] lg:ring-white/10 transition-transform active:scale-[0.98]"
                            style={{ animationDelay: '.38s' }}>
                            <img src="/2gis_icon.png" alt="2GIS" className="h-10 w-10 flex-shrink-0 rounded-xl" />
                            <span className="min-w-0 flex-1">
                                <span className="block text-sm font-bold text-gray-900">2GIS-də bizə baxın</span>
                                <span className="text-[11px] font-medium text-gray-500">Ünvan, marşrut və rəylər</span>
                            </span>
                            <span className="flex flex-shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold text-white" style={{ background: '#19AA1E' }}>
                                Aç
                                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M7 17 17 7M9 7h8v8" /></svg>
                            </span>
                        </button>
                    )}

                    {/* İş saatları */}
                    {hasHours && (
                        <div className="qv2-up mt-3 overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_rgba(16,24,40,.08)] ring-1 ring-black/[0.04] lg:shadow-[0_18px_40px_-18px_rgba(0,0,0,.55)] lg:ring-white/10" style={{ animationDelay: '.4s' }}>
                            <button type="button" onClick={() => hoursStruct?.mode !== 'everyday' && hoursStruct && setHoursOpen((v) => !v)}
                                className="flex w-full items-center gap-3 p-4 text-left">
                                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl" style={{ background: rgba(BRAND, 0.1), color: BRAND }}>
                                    <ClockIcon className="h-5 w-5" />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-bold text-gray-900">İş saatları</span>
                                    <span className="text-[12px] font-medium text-gray-500">
                                        {hoursStruct
                                            ? (hoursStruct.mode === 'everyday'
                                                ? `Hər gün ${hoursStruct.everyday?.open || ''} – ${hoursStruct.everyday?.close || ''}`
                                                : (status?.today ? `Bu gün ${status.today.open} – ${status.today.close}` : 'Bu gün bağlıdır'))
                                            : hoursText}
                                    </span>
                                </span>
                                {hoursStruct && hoursStruct.mode !== 'everyday' && (
                                    <span className="text-gray-400"><ChevronIcon open={hoursOpen} /></span>
                                )}
                            </button>
                            {hoursOpen && hoursStruct && hoursStruct.mode !== 'everyday' && (
                                <div className="border-t border-gray-100 px-4 py-2">
                                    {DAY_ORDER.map((k) => {
                                        const d = hoursStruct.days?.[k] || {};
                                        const isToday = k === todayKey;
                                        return (
                                            <div key={k} className={`flex items-center justify-between py-1.5 text-[13px] ${isToday ? 'font-bold' : ''}`}>
                                                <span className={isToday ? '' : 'text-gray-500'} style={isToday ? { color: BRAND } : undefined}>
                                                    {DAY_LABELS[k]}{isToday ? ' · bu gün' : ''}
                                                </span>
                                                {d.on && d.open && d.close
                                                    ? <span className="font-semibold text-gray-700">{d.open} – {d.close}</span>
                                                    : <span className="font-semibold text-rose-400">Bağlı</span>}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Footer */}
                    <div className="mt-8 flex flex-col items-center gap-1 text-center">
                        <a href="https://www.fiscalbusiness.net" target="_blank" rel="noopener noreferrer"
                            className="text-[11px] font-semibold text-gray-400 hover:text-gray-600 lg:text-white/60 lg:hover:text-white">
                            Powered by Fiscal POS
                        </a>
                        <a href="tel:+994552611199" className="text-[11px] text-gray-400 lg:text-white/50">Ətraflı məlumat: 055 261 11 99</a>
                    </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default QrCoverV2;
